/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, TextField, Typography } from '@mui/material';
import Swal from 'sweetalert2';
import MDButton from 'components/MDButton';
import { api } from '../../../api';
import { TEAL, buildPrescriptionPrintUrl, calculateBmi, getCurrentUserId, withUnit } from '../constants';

// Single-click prescription creation from an appointment. Vitals + remarks
// are all optional — if left blank, the prescription is still created (as a
// bare DRAFT the doctor can flesh out later from the Prescription screen).
export default function CreatePrescriptionFromAppointmentDialog({ appointment, onClose, onDone }) {
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [bmi, setBmi] = useState('');
  const [bpSystolic, setBpSystolic] = useState('');
  const [bpDiastolic, setBpDiastolic] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Auto-calculated from weight+height whenever both are present — never
  // overwrites a value the doctor typed in if only one of the two is set.
  useEffect(() => {
    const computed = calculateBmi(weight, height);
    if (computed) setBmi(computed);
  }, [weight, height]);

  const handleSubmit = async () => {
    const confirmResult = await Swal.fire({
      title: 'Create prescription?',
      text: 'A draft prescription will be created for this appointment. You can edit the full details afterwards.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: TEAL,
      confirmButtonText: 'Yes, Create',
      cancelButtonText: 'Cancel',
    });
    if (!confirmResult.isConfirmed) return;

    setSubmitting(true);
    try {
      const resp = await api.appointment.createPrescriptionFromAppointment(appointment.id, {
        // weight/height/bmi are free-text columns — the field only ever holds
        // the bare number (the unit shows as an adornment), so the unit is
        // appended here, once, right before it's sent.
        weight: withUnit(weight, 'kg'),
        height: withUnit(height, 'cm'),
        bmi: withUnit(bmi, 'kg/m²'),
        // bp_* are real INTEGER columns — must stay bare numbers.
        bp_systolic: bpSystolic ? Number(bpSystolic) : undefined,
        bp_diastolic: bpDiastolic ? Number(bpDiastolic) : undefined,
        remarks: remarks || undefined,
        created_by: getCurrentUserId(),
      });

      const prescriptionCode = resp?.data?.prescription_code;
      const printUrl = prescriptionCode ? buildPrescriptionPrintUrl(appointment, prescriptionCode) : null;

      if (printUrl) {
        const viewResult = await Swal.fire({
          icon: 'success',
          title: 'Prescription created',
          text: 'Open it now to review and print?',
          showCancelButton: true,
          confirmButtonColor: TEAL,
          confirmButtonText: 'View & Print',
          cancelButtonText: 'Close',
        });
        if (viewResult.isConfirmed) {
          window.open(printUrl, '_blank', 'noopener,noreferrer');
        }
      } else {
        Swal.fire({ icon: 'success', title: 'Prescription created', timer: 1800, showConfirmButton: false });
      }
      onDone?.();
    } catch {
      // generic error toast already shown by ApiClient
    } finally {
      setSubmitting(false);
    }
  };

  const vitalFieldSx = { minWidth: 150 };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Prescription</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2" color="textSecondary">
            Vitals and remarks below are optional — leave blank to create a bare draft.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Weight"
              size="small"
              fullWidth
              sx={vitalFieldSx}
              value={weight}
              onChange={e => setWeight(e.target.value)}
              InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
            />
            <TextField
              label="Height"
              size="small"
              fullWidth
              sx={vitalFieldSx}
              value={height}
              onChange={e => setHeight(e.target.value)}
              InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="BMI (auto)"
              size="small"
              fullWidth
              sx={vitalFieldSx}
              value={bmi}
              onChange={e => setBmi(e.target.value)}
              helperText={weight && height ? 'Auto-calculated from weight & height' : ' '}
              InputProps={{ endAdornment: <InputAdornment position="end">kg/m²</InputAdornment> }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="BP Systolic"
              size="small"
              fullWidth
              sx={vitalFieldSx}
              type="number"
              value={bpSystolic}
              onChange={e => setBpSystolic(e.target.value)}
              InputProps={{ endAdornment: <InputAdornment position="end">mmHg</InputAdornment> }}
            />
            <TextField
              label="BP Diastolic"
              size="small"
              fullWidth
              sx={vitalFieldSx}
              type="number"
              value={bpDiastolic}
              onChange={e => setBpDiastolic(e.target.value)}
              InputProps={{ endAdornment: <InputAdornment position="end">mmHg</InputAdornment> }}
            />
          </Box>
          <TextField
            label="Remarks (optional)"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="secondary">Cancel</MDButton>
        <MDButton onClick={handleSubmit} disabled={submitting} variant="gradient" color="logo">
          {submitting ? 'Creating…' : 'Create Prescription'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
